// utils/deviceInfo.ts

import FingerprintJS from '@fingerprintjs/fingerprintjs';
import logger from './logger';

/**
 * Device Fingerprinting using FingerprintJS
 * Better and more unique device identification
 */

let fpPromise: Promise<any> | null = null;

/**
 * Initialize FingerprintJS (call once)
 */
function initFingerprinting(): Promise<any> {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load();
    logger.log('🔍 FingerprintJS initialized');
  }
  return fpPromise;
}

/**
 * Generate device fingerprint using FingerprintJS
 */
export async function generateDeviceFingerprint(): Promise<string> {
  try {
    // Initialize FingerprintJS
    const fp = await initFingerprinting();
    
    // Get the fingerprint
    const result = await fp.get();
    
    logger.log('🔍 Device fingerprint generated:', result.visitorId);
    return result.visitorId;
  } catch (error) {
    logger.log('❌ FingerprintJS failed, using fallback:', error);
    
    // Fallback to simple hash if FingerprintJS fails
    const fallbackData = [
      navigator.userAgent,
      navigator.platform,
      window.screen.width.toString(),
      window.screen.height.toString(),
      navigator.language
    ].join('|');
    
    // Simple hash for fallback
    let hash = 0;
    for (let i = 0; i < fallbackData.length; i++) {
      const char = fallbackData.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return Math.abs(hash).toString(16);
  }
}

/**
 * Get complete device information for API
 */
export async function getDeviceInfo(): Promise<object> {
  try {
    // Get fingerprint from FingerprintJS
    const deviceId = await generateDeviceFingerprint();
    
    const deviceInfo = {
      // Primary device identifier (from FingerprintJS)
      deviceId: deviceId,
      
      // Browser information
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      vendor: navigator.vendor || 'unknown',
      
      // Screen information
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio || 1,
      
      // Hardware information
      hardwareConcurrency: navigator.hardwareConcurrency || 1,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      
      // Environment information
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      timezoneOffset: new Date().getTimezoneOffset(),
      cookieEnabled: navigator.cookieEnabled,
      
      // Session information
      timestamp: new Date().toISOString(),
      sessionId: Math.random().toString(36).substring(2)
    };

    logger.log('🔍 Device info collected with FingerprintJS:', {
      deviceId: deviceInfo.deviceId,
      platform: deviceInfo.platform,
      resolution: deviceInfo.screenResolution
    });

    return deviceInfo;
  } catch (error) {
    logger.log('❌ Error collecting device info:', error);
    
    // Minimal fallback
    return {
      deviceId: 'fallback-' + Date.now(),
      userAgent: navigator.userAgent || 'unknown',
      platform: navigator.platform || 'unknown',
      timestamp: new Date().toISOString(),
      error: 'device_info_collection_failed'
    };
  }
}

/**
 * Get lightweight device ID only (for quick calls)
 */
export async function getDeviceId(): Promise<string> {
  return await generateDeviceFingerprint();
}